import { useState, useEffect, useCallback, useMemo } from "react";
import { Plus, TrendingUp, Calendar, Crown, Loader2, MapPin, Home, User, Users, Trophy, ChevronDown, ChevronRight, ExternalLink, ChevronLeft } from "lucide-react";
import { getIconByName } from "@/components/team/performanceMetricsConfig";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Badge } from "@/components/ui/badge";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Link, useNavigate } from "react-router-dom";
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from "@/components/ui/collapsible";
import { SidebarProvider, SidebarTrigger } from "@/components/ui/sidebar";
import { AppSidebar } from "@/components/AppSidebar";
import { useAuth } from "@/hooks/useAuth";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import type { TeamPerformanceMetric, TeamMemberPerformance } from "@/types/team";
import type { D2DUnit, D2DArea } from "@/types/d2d";
import type { Customer } from "@/types/customer";

interface PerformanceWithDetails extends TeamMemberPerformance {
  metric?: TeamPerformanceMetric;
  unit?: {
    id: string;
    unit_name?: string;
    house_id: string;
    house?: {
      id: string;
      house_number: string;
      street?: {
        id: string;
        name: string;
        area_id: string;
        area?: {
          id: string;
          name: string;
        };
      };
    };
  };
}

interface UnitWithLocation extends D2DUnit {
  house?: {
    id: string;
    house_number: string;
    street?: {
      id: string;
      name: string;
      area_id: string;
      area?: {
        id: string;
        name: string;
      };
    };
  };
}

const Performance = () => {
  const formatPeriodKey = (year: number, monthIndex: number) => {
    return `${year}-${String(monthIndex + 1).padStart(2, '0')}`;
  };

  const getCurrentPeriodKey = () => {
    const now = new Date();
    return formatPeriodKey(now.getFullYear(), now.getMonth());
  };

  const toLocalDateString = (d: Date) => {
    const yyyy = d.getFullYear();
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    return `${yyyy}-${mm}-${dd}`;
  };

  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [teamId, setTeamId] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<TeamPerformanceMetric[]>([]);
  const [performances, setPerformances] = useState<PerformanceWithDetails[]>([]);
  const [units, setUnits] = useState<UnitWithLocation[]>([]);
  const [areas, setAreas] = useState<D2DArea[]>([]);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [expandedPerformances, setExpandedPerformances] = useState<Set<string>>(new Set());
  const [selectedPeriod, setSelectedPeriod] = useState<string>(() => {
    // Default to current month
    return getCurrentPeriodKey();
  });
  
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [selectedMetricIds, setSelectedMetricIds] = useState<string[]>([]);
  const [assignmentTab, setAssignmentTab] = useState<"unit" | "customer">("unit");
  const [selectedAreaId, setSelectedAreaId] = useState("");
  const [selectedUnitId, setSelectedUnitId] = useState("");
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [notes, setNotes] = useState("");
  const [submitting, setSubmitting] = useState(false);

  // Initialize sidebar state from localStorage
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const saved = localStorage.getItem('sidebarState');
    return saved ? JSON.parse(saved) : true;
  });

  useEffect(() => {
    localStorage.setItem('sidebarState', JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  // Load team ID and data
  const loadData = useCallback(async () => {
    if (!user) return;

    try {
      setLoading(true);

      // Get user's team
      const { data: profile } = await supabase
        .from('profiles')
        .select('team_id')
        .eq('user_id', user.id)
        .single();

      if (!profile?.team_id) {
        setLoading(false);
        return;
      }

      setTeamId(profile.team_id);

      // Load metrics, performances, areas, and customers in parallel
      // Note: We'll load unit details separately based on performance unit_ids
      const [metricsResult, performancesResult, areasResult, customersResult] = await Promise.all([
        supabase
          .from('team_performance_metrics')
          .select('*')
          .eq('team_id', profile.team_id)
          .eq('is_active', true)
          .order('sort_order', { ascending: true }),
        supabase
          .from('team_member_performance')
          .select('*')
          .eq('user_id', user.id)
          .order('recorded_at', { ascending: false })
          .limit(100),
        supabase
          .from('d2d_areas')
          .select('*')
          .eq('created_by', user.id)
          .order('name'),
        supabase
          .from('customers')
          .select('*')
          .eq('user_id', user.id)
          .order('first_name')
      ]);

      if (metricsResult.data) {
        setMetrics(metricsResult.data);
      }

      // Get unique unit IDs from performances that have unit_id
      const unitIds = [...new Set(
        performancesResult.data
          ?.filter(p => p.unit_id)
          .map(p => p.unit_id) || []
      )];

      // Load unit details for these specific units
      let unitsData: UnitWithLocation[] = [];
      if (unitIds.length > 0) {
        const { data: unitsResult } = await supabase
          .from('d2d_units')
          .select(`
            id,
            unit_name,
            tags,
            house_id,
            house:d2d_houses!inner(
              id,
              house_number,
              street:d2d_streets!inner(
                id,
                name,
                area_id,
                area:d2d_areas!inner(
                  id,
                  name
                )
              )
            )
          `)
          .in('id', unitIds);
        
        if (unitsResult) {
          unitsData = unitsResult as UnitWithLocation[];
        }
      }

      if (performancesResult.data) {
        // Enrich performances with metric details and unit info
         const enrichedPerformances = performancesResult.data.map(perf => {
           const unit = unitsData.find(u => u.id === perf.unit_id);
           return {
             ...perf,
             metric: metricsResult.data?.find(m => m.id === perf.metric_id),
             unit: unit ? {
               id: unit.id,
               unit_name: unit.unit_name,
               house_id: unit.house_id,
               house: unit.house
             } : undefined
           };
         });
        setPerformances(enrichedPerformances);
      }

      // Also load units for the dialog (user's units for new performance creation)
      const { data: userUnits } = await supabase
        .from('d2d_units')
        .select(`
          id,
          unit_name,
          tags,
          house_id,
          house:d2d_houses!inner(
            id,
            house_number,
            street:d2d_streets!inner(
              id,
              name,
              area_id,
              area:d2d_areas!inner(
                id,
                name
              )
            )
          )
        `)
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false })
        .limit(200);

      if (userUnits) {
        setUnits(userUnits as UnitWithLocation[]);
      }

      if (areasResult.data) {
        setAreas(areasResult.data);
        // Set active area as default if exists
        const activeArea = areasResult.data.find(a => a.is_active);
        if (activeArea) {
          setSelectedAreaId(activeArea.id);
        }
      }

      if (customersResult.data) {
        setCustomers(customersResult.data as Customer[]);
      }

    } catch (error) {
      console.error('Error loading performance data:', error);
      toast.error('Fehler beim Laden der Daten');
    } finally {
      setLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  // Toggle metric selection
  const toggleMetric = (metricId: string) => {
    setSelectedMetricIds(prev => 
      prev.includes(metricId) 
        ? prev.filter(id => id !== metricId)
        : [...prev, metricId]
    );
  };

  // Handle adding performance records
  const handleAddPerformance = async () => {
    if (!user || selectedMetricIds.length === 0) {
      toast.error('Bitte wählen Sie mindestens eine Leistung aus.');
      return;
    }

    // Require either unit or customer selection
    if (assignmentTab === "unit" && !selectedUnitId) {
      toast.error('Bitte wählen Sie eine Einheit aus.');
      return;
    }
    if (assignmentTab === "customer" && !selectedCustomerId) {
      toast.error('Bitte wählen Sie einen Kunden aus.');
      return;
    }

    setSubmitting(true);
    try {
      const today = new Date().toISOString().split('T')[0];

      // Create performance records for each selected metric
      const records = selectedMetricIds.map(metricId => {
        const metric = metrics.find(m => m.id === metricId);
        // Include customer ID in notes if customer was selected (for later retrieval)
        let performanceNotes = notes.trim() || null;
        if (assignmentTab === "customer" && selectedCustomerId) {
          const customer = customers.find(c => c.id === selectedCustomerId);
          const customerName = customer ? `${customer.first_name}${customer.last_name ? ` ${customer.last_name}` : ''}` : '';
          performanceNotes = `[customer:${selectedCustomerId}] Kunde: ${customerName}${notes ? ` - ${notes}` : ''}`;
        }
        return {
          metric_id: metricId,
          user_id: user.id,
          points: metric?.points_value || 1,
          notes: performanceNotes,
          unit_id: assignmentTab === "unit" ? selectedUnitId : null,
          recorded_at: today,
          recorded_by: user.id
        };
      });

      const { error: insertError } = await supabase
        .from('team_member_performance')
        .insert(records);

      if (insertError) throw insertError;

      // Tag the unit with WIN if unit was selected
      if (assignmentTab === "unit" && selectedUnitId) {
        const { data: unitData } = await supabase
          .from('d2d_units')
          .select('tags')
          .eq('id', selectedUnitId)
          .single();

        const currentTags = unitData?.tags || [];
        if (!currentTags.includes('WIN')) {
          await supabase
            .from('d2d_units')
            .update({ 
              tags: [...currentTags, 'WIN'],
              updated_at: new Date().toISOString()
            })
            .eq('id', selectedUnitId);
        }
      }

      // If customer was selected, add note to customer
      if (assignmentTab === "customer" && selectedCustomerId) {
        const customer = customers.find(c => c.id === selectedCustomerId);
        const metricsNames = selectedMetricIds.map(id => metrics.find(m => m.id === id)?.name).filter(Boolean).join(', ');
        const performanceNote = `[${new Date().toLocaleDateString('de-DE')} ${new Date().toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}] Leistung erfasst: ${metricsNames}${notes ? ` - ${notes}` : ''}`;
        
        await supabase
          .from('customers')
          .update({ 
            notes: customer?.notes ? `${customer.notes}\n${performanceNote}` : performanceNote,
            updated_at: new Date().toISOString()
          })
          .eq('id', selectedCustomerId);
      }

      toast.success(`${selectedMetricIds.length} Leistung(en) erfasst`);
      setIsDialogOpen(false);
      setSelectedMetricIds([]);
      setSelectedUnitId("");
      setSelectedCustomerId("");
      setNotes("");
      
      // Reload data
      loadData();

    } catch (error) {
      console.error('Error recording performance:', error);
      toast.error('Fehler beim Erfassen der Leistung');
    } finally {
      setSubmitting(false);
    }
  };

  // Filter units by selected area
  const filteredUnits = selectedAreaId 
    ? units.filter(unit => unit.house?.street?.area_id === selectedAreaId)
    : units;

  // Parse selected period into date components
  const selectedDate = useMemo(() => {
    if (selectedPeriod === 'last-year') {
      return null;
    }
    const [year, month] = selectedPeriod.split('-').map(Number);
    return { year, month: month - 1 }; // month is 0-indexed
  }, [selectedPeriod]);

  // Navigate to previous/next month
  const navigatePeriod = (direction: 'prev' | 'next') => {
    if (!selectedDate) {
      // If on "last year" view, go to current month
      setSelectedPeriod(getCurrentPeriodKey());
      return;
    }
    
    const newDate = new Date(selectedDate.year, selectedDate.month + (direction === 'next' ? 1 : -1), 1);
    const newPeriod = formatPeriodKey(newDate.getFullYear(), newDate.getMonth());
    
    // Check if within valid range (last 12 months)
    const now = new Date();
    const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    
    if (newDate <= now && newDate >= twelveMonthsAgo) {
      setSelectedPeriod(newPeriod);
    }
  };

  // Check if can navigate
  const canNavigatePrev = useMemo(() => {
    if (!selectedDate) return true;
    const now = new Date();
    const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
    const current = new Date(selectedDate.year, selectedDate.month, 1);
    return current > twelveMonthsAgo;
  }, [selectedDate]);

  const canNavigateNext = useMemo(() => {
    if (!selectedDate) return true;
    const now = new Date();
    const current = new Date(selectedDate.year, selectedDate.month, 1);
    return current < new Date(now.getFullYear(), now.getMonth(), 1);
  }, [selectedDate]);

  // Get formatted month and year
  const displayMonth = useMemo(() => {
    if (!selectedDate) return 'Letztes Jahr';
    return new Date(selectedDate.year, selectedDate.month, 1).toLocaleDateString('de-DE', { month: 'long' });
  }, [selectedDate]);

  const displayYear = useMemo(() => {
    if (!selectedDate) return '(12 Monate)';
    return selectedDate.year.toString();
  }, [selectedDate]);

  // Generate year options for dropdown
  const yearOptions = useMemo(() => {
    const now = new Date();
    const years = new Set<number>();
    for (let i = 0; i < 12; i++) {
      const date = new Date(now.getFullYear(), now.getMonth() - i, 1);
      years.add(date.getFullYear());
    }
    return Array.from(years).sort((a, b) => b - a);
  }, []);

  // Generate month options for selected year
  const monthOptionsForYear = useMemo(() => {
    if (!selectedDate) return [];
    const now = new Date();
    const months: { value: number; label: string; disabled: boolean }[] = [];
    
    for (let m = 0; m < 12; m++) {
      const date = new Date(selectedDate.year, m, 1);
      const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
      const isDisabled = date > now || date < twelveMonthsAgo;
      
      months.push({
        value: m,
        label: date.toLocaleDateString('de-DE', { month: 'long' }),
        disabled: isDisabled
      });
    }
    return months;
  }, [selectedDate]);

  // Filter performances based on selected period
  const filteredPerformances = useMemo(() => {
    if (selectedPeriod === 'last-year') {
      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      const oneYearAgoStr = toLocalDateString(oneYearAgo);
      return performances.filter(p => p.recorded_at >= oneYearAgoStr);
    }
    return performances.filter(p => p.recorded_at.startsWith(selectedPeriod));
  }, [performances, selectedPeriod]);

  const metricStats = metrics.map(metric => {
    const count = filteredPerformances.filter(p => p.metric_id === metric.id).length;
    const points = filteredPerformances
      .filter(p => p.metric_id === metric.id)
      .reduce((sum, p) => sum + p.points, 0);
    return { metric, count, points };
  });

  const totalPoints = filteredPerformances.reduce((sum, p) => sum + p.points, 0);

  // Get unit display info with location
  const getUnitLabel = (unit: UnitWithLocation) => {
    const baseName = unit.unit_name || `Einheit`;
    const housePart = unit.house?.house_number ? ` Nr. ${unit.house.house_number}` : '';
    const streetPart = unit.house?.street?.name ? `, ${unit.house.street.name}` : '';
    return `${baseName}${housePart}${streetPart}`;
  };

  // Get customer display info
  const getCustomerLabel = (customer: Customer) => {
    return `${customer.first_name}${customer.last_name ? ` ${customer.last_name}` : ''}`;
  };

  // Toggle expanded state for a performance
  const toggleExpanded = (perfId: string) => {
    setExpandedPerformances(prev => {
      const newSet = new Set(prev);
      if (newSet.has(perfId)) {
        newSet.delete(perfId);
      } else {
        newSet.add(perfId);
      }
      return newSet;
    });
  };

  // Navigate to unit in running list
  const navigateToUnit = (perf: PerformanceWithDetails) => {
    if (perf.unit?.house?.street?.area) {
      // Navigate to running list with area context
      navigate('/running-list');
    }
  };

  // Get source info for a performance
  const getSourceInfo = (perf: PerformanceWithDetails) => {
    if (perf.unit) {
      const streetName = perf.unit.house?.street?.name || '';
      const houseNumber = perf.unit.house?.house_number || '';
      const areaName = perf.unit.house?.street?.area?.name || '';
      const areaId = perf.unit.house?.street?.area_id || null;
      const streetId = perf.unit.house?.street?.id || null;
      const houseId = perf.unit.house?.id || null;
      const unitName = perf.unit.unit_name || '';
      
      const labelParts = [];
      if (streetName) labelParts.push(streetName);
      if (houseNumber) labelParts.push(houseNumber);
      if (unitName) labelParts.push(`- ${unitName}`);
      
      return {
        type: 'unit' as const,
        label: labelParts.join(' ') || 'Laufliste Einheit',
        sublabel: areaName ? `Gebiet: ${areaName}` : 'Laufliste',
        icon: Home,
        unitId: perf.unit.id,
        areaId,
        streetId,
        houseId,
        customerId: null as string | null
      };
    }
    
    // Check notes for customer info with ID pattern [customer:uuid]
    if (perf.notes?.includes('[customer:')) {
      const customerIdMatch = perf.notes.match(/\[customer:([a-f0-9-]+)\]/);
      const customerId = customerIdMatch ? customerIdMatch[1] : null;
      // Extract customer name after "Kunde: "
      const customerNameMatch = perf.notes.match(/Kunde:\s*([^-\n]+)/);
      const customerName = customerNameMatch ? customerNameMatch[1].trim() : 'Kunde';
      
      return {
        type: 'customer' as const,
        label: customerName,
        sublabel: 'Kundenverwaltung',
        icon: User,
        unitId: null as string | null,
        areaId: null as string | null,
        streetId: null as string | null,
        houseId: null as string | null,
        customerId
      };
    }
    
    // Legacy: Check notes for customer info without ID
    if (perf.notes?.includes('Kunde:')) {
      const customerName = perf.notes.replace('Kunde: ', '').split('\n')[0].split(' - ')[0].trim();
      return {
        type: 'customer' as const,
        label: customerName,
        sublabel: 'Kundenverwaltung',
        icon: User,
        unitId: null as string | null,
        areaId: null as string | null,
        streetId: null as string | null,
        houseId: null as string | null,
        customerId: null as string | null
      };
    }
    
    // If there's notes but no unit/customer, show notes as info
    if (perf.notes) {
      return {
        type: 'note' as const,
        label: perf.notes,
        sublabel: 'Notiz',
        icon: MapPin,
        unitId: null as string | null,
        areaId: null as string | null,
        streetId: null as string | null,
        houseId: null as string | null,
        customerId: null as string | null
      };
    }
    
    return null;
  };

  if (loading) {
    return (
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          <div className="flex-1 flex items-center justify-center">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (!teamId) {
    return (
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
              <SidebarTrigger className="ml-2" />
              <div className="flex-1 px-4">
                <h1 className="text-lg font-bold">Leistungserfassung</h1>
              </div>
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-md">
                <CardContent className="text-center py-8">
                  <Trophy className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Kein Team zugewiesen</h3>
                  <p className="text-muted-foreground">
                    Sie müssen einem Team beitreten, um Leistungen zu erfassen.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  if (metrics.length === 0) {
    return (
      <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
        <div className="min-h-screen flex w-full bg-gradient-subtle">
          <AppSidebar />
          <div className="flex-1 flex flex-col">
            <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
              <SidebarTrigger className="ml-2" />
              <div className="flex-1 px-4">
                <h1 className="text-lg font-bold">Leistungserfassung</h1>
              </div>
            </header>
            <div className="flex-1 flex items-center justify-center p-4">
              <Card className="max-w-md">
                <CardContent className="text-center py-8">
                  <Trophy className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                  <h3 className="text-lg font-semibold mb-2">Keine Metriken definiert</h3>
                  <p className="text-muted-foreground">
                    Ihr Teamleiter hat noch keine Leistungsmetriken für Ihr Team definiert.
                  </p>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </SidebarProvider>
    );
  }

  return (
    <SidebarProvider open={isSidebarOpen} onOpenChange={setIsSidebarOpen}>
      <div className="min-h-screen flex w-full bg-gradient-subtle">
        <AppSidebar />
        
        <div className="flex-1 flex flex-col">
          {/* Header */}
          <header className="h-12 flex items-center border-b bg-card/80 backdrop-blur-sm sticky top-0 z-40">
            <SidebarTrigger className="ml-2" />
            <div className="flex-1 flex items-center justify-between px-4">
              <h1 className="text-lg font-bold">Leistungserfassung</h1>
              <div className="flex items-center gap-2">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                  <DialogTrigger asChild>
                    <Button 
                      variant="outline"
                      className="gap-2 bg-card text-card-foreground border shadow-sm hover:bg-accent" 
                      size="sm"
                    >
                      <Plus className="h-4 w-4" />
                      <span className="hidden sm:inline">Leistung erfassen</span>
                      <span className="sm:hidden">Neu</span>
                    </Button>
                  </DialogTrigger>
                  <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Leistung erfassen</DialogTitle>
                      <DialogDescription>
                        Wählen Sie die Leistungen und die zugehörige Einheit aus.
                      </DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                      {/* Metric Selection */}
                      <div className="space-y-2">
                        <Label>Leistungen (mehrere auswählbar)</Label>
                        <div className="grid grid-cols-2 gap-2">
                          {metrics.map(metric => (
                            <Button
                              key={metric.id}
                              type="button"
                              variant={selectedMetricIds.includes(metric.id) ? "default" : "outline"}
                              className="justify-start h-auto py-2 px-3"
                              onClick={() => toggleMetric(metric.id)}
                            >
                              <div className="text-left">
                                <div className="font-medium text-sm">{metric.name}</div>
                                <div className="text-xs opacity-70">{metric.points_value} Punkte</div>
                              </div>
                            </Button>
                          ))}
                        </div>
                        {selectedMetricIds.length > 0 && (
                          <p className="text-xs text-muted-foreground">
                            {selectedMetricIds.length} Leistung(en) ausgewählt
                          </p>
                        )}
                      </div>

                      {/* Assignment Selection with Tabs */}
                      <Tabs value={assignmentTab} onValueChange={(v) => {
                        setAssignmentTab(v as "unit" | "customer");
                        // Reset selections when switching tabs
                        if (v === "customer") {
                          setSelectedUnitId("");
                          setSelectedAreaId("");
                        } else {
                          setSelectedCustomerId("");
                          // Reset to active area
                          const activeArea = areas.find(a => a.is_active);
                          if (activeArea) setSelectedAreaId(activeArea.id);
                        }
                      }}>
                        <TabsList className="grid w-full grid-cols-2">
                          <TabsTrigger value="unit" className="gap-2">
                            <MapPin className="h-3 w-3" />
                            Gebiet / Einheit
                          </TabsTrigger>
                          <TabsTrigger value="customer" className="gap-2">
                            <User className="h-3 w-3" />
                            Kunde
                          </TabsTrigger>
                        </TabsList>

                        <TabsContent value="unit" className="space-y-3 mt-3">
                          {/* Area Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs">Gebiet auswählen</Label>
                            <Select value={selectedAreaId} onValueChange={(v) => {
                              setSelectedAreaId(v);
                              setSelectedUnitId(""); // Reset unit when area changes
                            }}>
                              <SelectTrigger>
                                <SelectValue placeholder="Gebiet auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {areas.map(area => (
                                  <SelectItem key={area.id} value={area.id}>
                                    <div className="flex items-center gap-2">
                                      <MapPin className="h-3 w-3" />
                                      {area.name}
                                      {area.plz && <span className="text-xs text-muted-foreground">({area.plz})</span>}
                                      {area.is_active && (
                                        <Badge variant="default" className="text-xs ml-1">Aktiv</Badge>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                              </SelectContent>
                            </Select>
                          </div>

                          {/* Unit Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs">Einheit zuordnen</Label>
                            <Select 
                              value={selectedUnitId} 
                              onValueChange={setSelectedUnitId}
                              disabled={!selectedAreaId}
                            >
                              <SelectTrigger>
                                <SelectValue placeholder={selectedAreaId ? "Einheit auswählen" : "Zuerst Gebiet wählen"} />
                              </SelectTrigger>
                              <SelectContent>
                                {filteredUnits.map(unit => (
                                  <SelectItem key={unit.id} value={unit.id}>
                                    <div className="flex items-center gap-2">
                                      <Home className="h-3 w-3" />
                                      {getUnitLabel(unit)}
                                      {unit.tags?.includes('WIN') && (
                                        <Badge variant="secondary" className="text-xs ml-1">WIN</Badge>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                                {filteredUnits.length === 0 && selectedAreaId && (
                                  <div className="p-2 text-xs text-muted-foreground text-center">
                                    Keine Einheiten in diesem Gebiet
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </TabsContent>

                        <TabsContent value="customer" className="space-y-3 mt-3">
                          {/* Customer Selection */}
                          <div className="space-y-2">
                            <Label className="text-xs">Kunde auswählen</Label>
                            <Select value={selectedCustomerId} onValueChange={setSelectedCustomerId}>
                              <SelectTrigger>
                                <SelectValue placeholder="Kunde auswählen" />
                              </SelectTrigger>
                              <SelectContent>
                                {customers.map(customer => (
                                  <SelectItem key={customer.id} value={customer.id}>
                                    <div className="flex items-center gap-2">
                                      <User className="h-3 w-3" />
                                      {getCustomerLabel(customer)}
                                      {customer.email && (
                                        <span className="text-xs text-muted-foreground">({customer.email})</span>
                                      )}
                                    </div>
                                  </SelectItem>
                                ))}
                                {customers.length === 0 && (
                                  <div className="p-2 text-xs text-muted-foreground text-center">
                                    Keine Kunden vorhanden
                                  </div>
                                )}
                              </SelectContent>
                            </Select>
                          </div>
                        </TabsContent>
                      </Tabs>

                      {/* Notes */}
                      <div className="space-y-2">
                        <Label className="text-xs">Kommentar (optional)</Label>
                        <Textarea
                          value={notes}
                          onChange={(e) => setNotes(e.target.value)}
                          placeholder="Zusätzliche Informationen..."
                          rows={2}
                        />
                      </div>

                      <div className="flex justify-end gap-2 pt-2">
                        <Button variant="outline" onClick={() => setIsDialogOpen(false)}>
                          Abbrechen
                        </Button>
                        <Button 
                          onClick={handleAddPerformance}
                          disabled={submitting || selectedMetricIds.length === 0 || (assignmentTab === "unit" ? !selectedUnitId : !selectedCustomerId)}
                        >
                          {submitting ? (
                            <Loader2 className="h-4 w-4 animate-spin mr-2" />
                          ) : null}
                          Erfassen
                        </Button>
                      </div>
                    </div>
                  </DialogContent>
                </Dialog>
              </div>
            </div>
          </header>

          {/* Content */}
          <div className="flex-1 overflow-auto">
            <div className="container mx-auto px-4 py-4 md:py-8">
              <div className="max-w-4xl mx-auto">
                
                {/* Period Stats */}
                <div className="mb-6">
                  <div className="flex items-center gap-3 mb-4">
                    {/* Previous Month Button */}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => navigatePeriod('prev')}
                      disabled={!canNavigatePrev}
                    >
                      <ChevronLeft className="h-4 w-4" />
                    </Button>

                    {/* Month & Year Display */}
                    <div className="flex items-center gap-2">
                      <Calendar className="h-5 w-5 text-muted-foreground" />
                      
                      {/* Month Dropdown */}
                      {selectedDate ? (
                        <Select 
                          value={selectedDate.month.toString()} 
                          onValueChange={(v) => {
                            const newPeriod = `${selectedDate.year}-${(parseInt(v) + 1).toString().padStart(2, '0')}`;
                            setSelectedPeriod(newPeriod);
                          }}
                        >
                          <SelectTrigger className="w-auto border-none shadow-none p-0 h-auto hover:bg-muted/50 rounded px-2 py-1 focus:ring-0">
                            <span className="text-lg font-semibold">{displayMonth}</span>
                          </SelectTrigger>
                          <SelectContent className="bg-popover z-50">
                            {monthOptionsForYear.map(m => (
                              <SelectItem 
                                key={m.value} 
                                value={m.value.toString()}
                                disabled={m.disabled}
                              >
                                {m.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      ) : (
                        <span className="text-lg font-semibold px-2 py-1">{displayMonth}</span>
                      )}

                      {/* Year Dropdown */}
                      <Select 
                        value={selectedDate ? selectedDate.year.toString() : 'last-year'} 
                        onValueChange={(v) => {
                          if (v === 'last-year') {
                            setSelectedPeriod('last-year');
                          } else {
                            const year = parseInt(v);
                            const now = new Date();
                            const twelveMonthsAgo = new Date(now.getFullYear(), now.getMonth() - 11, 1);
                            
                            // Try to keep current month, otherwise find valid month
                            let targetMonth = selectedDate?.month ?? now.getMonth();
                            let targetDate = new Date(year, targetMonth, 1);
                            
                            // Check if target date is valid (within 12 month range)
                            if (targetDate > now) {
                              // Future - go to current month
                              targetMonth = now.getMonth();
                            } else if (targetDate < twelveMonthsAgo) {
                              // Too old - go to oldest valid month in that year
                              // Find the first valid month
                              for (let m = 0; m < 12; m++) {
                                const checkDate = new Date(year, m, 1);
                                if (checkDate >= twelveMonthsAgo && checkDate <= now) {
                                  targetMonth = m;
                                  break;
                                }
                              }
                            }
                            
                            const newPeriod = `${year}-${(targetMonth + 1).toString().padStart(2, '0')}`;
                            setSelectedPeriod(newPeriod);
                          }
                        }}
                      >
                        <SelectTrigger className="w-auto border-none shadow-none p-0 h-auto hover:bg-muted/50 rounded px-2 py-1 focus:ring-0">
                          <span className="text-lg font-semibold text-muted-foreground">{displayYear}</span>
                        </SelectTrigger>
                        <SelectContent className="bg-popover z-50">
                          {yearOptions.map(year => (
                            <SelectItem key={year} value={year.toString()}>
                              {year}
                            </SelectItem>
                          ))}
                          <SelectItem value="last-year">
                            Letztes Jahr (12 Monate)
                          </SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Next Month Button */}
                    <Button 
                      variant="ghost" 
                      size="icon" 
                      className="h-8 w-8"
                      onClick={() => navigatePeriod('next')}
                      disabled={!canNavigateNext}
                    >
                      <ChevronRight className="h-4 w-4" />
                    </Button>
                  </div>

                  {/* Total Points - moved to top */}
                  <Card className="mb-4">
                    <CardContent className="p-4">
                      <div className="flex items-center gap-3">
                        <div className="p-2 rounded-lg bg-gradient-to-r from-primary to-primary/80">
                          <TrendingUp className="h-5 w-5 text-primary-foreground" />
                        </div>
                        <div>
                          <p className="text-sm font-medium text-muted-foreground">Gesamtpunkte</p>
                          <p className="text-2xl font-bold">{totalPoints}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  {/* Metric Cards */}
                  <div className="grid grid-cols-2 lg:grid-cols-3 gap-3">
                    {metricStats.map(({ metric, count, points }) => {
                      const MetricIcon = getIconByName(metric.icon || 'trophy');
                      return (
                      <Card key={metric.id}>
                        <CardContent className="p-4">
                          <div className="flex items-center gap-3">
                            <div className="p-2 rounded-lg bg-primary/10">
                              <MetricIcon className="h-4 w-4 text-primary" />
                            </div>
                            <div>
                              <p className="text-xs font-medium text-muted-foreground">{metric.name}</p>
                              <p className="text-xl font-bold">{count}</p>
                              <p className="text-xs text-muted-foreground">{points} Punkte</p>
                            </div>
                          </div>
                        </CardContent>
                      </Card>
                    );
                    })}
                  </div>
                </div>

                {/* Recent Performances - Grouped by Metric Type */}
                {filteredPerformances.length > 0 && (
                  <div className="mb-6">
                    <h3 className="text-base font-semibold mb-3">Leistungen im Zeitraum</h3>
                    <div className="space-y-2">
                      {/* Group performances by metric */}
                      {metrics.map(metric => {
                        const metricPerformances = filteredPerformances.filter(p => p.metric_id === metric.id);
                        if (metricPerformances.length === 0) return null;
                        
                        const MetricIcon = getIconByName(metric.icon || 'trophy');
                        const isExpanded = expandedPerformances.has(metric.id);
                        const totalMetricPoints = metricPerformances.reduce((sum, p) => sum + p.points, 0);
                        
                        return (
                          <Card key={metric.id} className="overflow-hidden">
                            <Collapsible open={isExpanded} onOpenChange={() => toggleExpanded(metric.id)}>
                              <CollapsibleTrigger asChild>
                                <CardContent className="p-3 cursor-pointer hover:bg-muted/50 transition-colors">
                                  <div className="flex items-center justify-between gap-3">
                                    <div className="flex items-center gap-3">
                                      <div className="p-1.5 rounded bg-primary/10">
                                        <MetricIcon className="h-4 w-4 text-primary" />
                                      </div>
                                      <div className="flex-1">
                                        <p className="text-sm font-medium">{metric.name}</p>
                                        <p className="text-xs text-muted-foreground">
                                          {metricPerformances.length}x diesen Monat
                                        </p>
                                      </div>
                                    </div>
                                    <div className="flex items-center gap-2">
                                      <Badge variant="secondary">{totalMetricPoints} Pkt.</Badge>
                                      {isExpanded ? (
                                        <ChevronDown className="h-4 w-4 text-muted-foreground" />
                                      ) : (
                                        <ChevronRight className="h-4 w-4 text-muted-foreground" />
                                      )}
                                    </div>
                                  </div>
                                </CardContent>
                              </CollapsibleTrigger>
                              
                              <CollapsibleContent>
                                <div className="px-3 pb-3 pt-0">
                                  <div className="border-t pt-3 space-y-2">
                                    {metricPerformances.map(perf => {
                                      const sourceInfo = getSourceInfo(perf);
                                      return (
                                        <div key={perf.id} className="flex items-center gap-3 p-2 rounded-lg bg-muted/30">
                                          <div className="flex-1">
                                            <p className="text-xs font-medium">
                                              {new Date(perf.recorded_at).toLocaleDateString('de-DE', {
                                                weekday: 'short',
                                                day: '2-digit',
                                                month: '2-digit',
                                                year: 'numeric'
                                              })}
                                            </p>
                                            {sourceInfo ? (
                                              <div className="mt-1">
                                                {sourceInfo.type === 'unit' || sourceInfo.type === 'customer' ? (
                                                  <Button
                                                    variant="link"
                                                    className="h-auto p-0 text-xs text-muted-foreground hover:text-primary flex items-center"
                                                    onClick={async (e) => {
                                                      e.stopPropagation();

                                                      if (sourceInfo.type === 'unit') {
                                                        if (
                                                          sourceInfo.areaId &&
                                                          sourceInfo.streetId &&
                                                          sourceInfo.houseId &&
                                                          sourceInfo.unitId
                                                        ) {
                                                          navigate('/running-list', {
                                                            state: {
                                                              selectedUnitId: sourceInfo.unitId,
                                                              selectedAreaId: sourceInfo.areaId,
                                                              selectedStreetId: sourceInfo.streetId,
                                                              selectedHouseId: sourceInfo.houseId,
                                                            },
                                                          });
                                                        } else {
                                                          // Fallback (sollte nur bei sehr alten/inkonsistenten Daten passieren)
                                                          navigate('/running-list');
                                                        }
                                                        return;
                                                      }

                                                      if (sourceInfo.type === 'customer') {
                                                        if (sourceInfo.customerId) {
                                                          navigate(`/customers?customer=${sourceInfo.customerId}`);
                                                        } else {
                                                          navigate('/customers');
                                                        }
                                                      }
                                                    }}
                                                  >
                                                    <sourceInfo.icon className="h-3 w-3 mr-1 flex-shrink-0" />
                                                    <span className="truncate">{sourceInfo.label}</span>
                                                    <ExternalLink className="h-3 w-3 ml-1 flex-shrink-0" />
                                                  </Button>
                                                ) : (
                                                  <p className="text-xs text-muted-foreground flex items-center gap-1">
                                                    <sourceInfo.icon className="h-3 w-3 flex-shrink-0" />
                                                    <span>{sourceInfo.label}</span>
                                                  </p>
                                                )}
                                                {sourceInfo.sublabel && sourceInfo.type !== 'note' && (
                                                  <p className="text-xs text-muted-foreground/70 ml-4">{sourceInfo.sublabel}</p>
                                                )}
                                              </div>
                                            ) : (
                                              <div className="mt-1">
                                                <p className="text-xs text-amber-600 flex items-center gap-1">
                                                  <MapPin className="h-3 w-3" />
                                                  Ohne Zuordnung erfasst
                                                </p>
                                                {perf.notes && (
                                                  <p className="text-xs text-muted-foreground mt-1">Notiz: "{perf.notes}"</p>
                                                )}
                                              </div>
                                            )}
                                            {perf.notes && sourceInfo?.type !== 'note' && !perf.notes.includes('Kunde:') && !perf.notes.includes('[customer:') && (
                                              <p className="text-xs text-muted-foreground mt-1 italic">"{perf.notes}"</p>
                                            )}
                                          </div>
                                          <Badge variant="outline" className="text-xs">{perf.points} Pkt.</Badge>
                                        </div>
                                      );
                                    })}
                                  </div>
                                </div>
                              </CollapsibleContent>
                            </Collapsible>
                          </Card>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* Empty State */}
                {performances.length === 0 && (
                  <Card>
                    <CardContent className="text-center py-12">
                      <TrendingUp className="mx-auto h-12 w-12 text-muted-foreground mb-4" />
                      <h3 className="text-lg font-semibold mb-2">Keine Leistungen erfasst</h3>
                      <p className="text-muted-foreground mb-4">
                        Erfassen Sie Ihre erste Leistung, um mit dem Tracking zu beginnen.
                      </p>
                      <Button className="gap-2" onClick={() => setIsDialogOpen(true)}>
                        <Plus className="h-4 w-4" />
                        Erste Leistung erfassen
                      </Button>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </SidebarProvider>
  );
};

export default Performance;
